import _ from 'lodash';
import React from 'react';
import {useDispatch} from 'react-redux';

import {useSelector} from '../../state/hooks';
import {getReportPart, getReportRef} from '../../state/reports/selectors';
import {setBlocks} from '../../state/views/report/actions';
import {useReportState} from './../../pages/ReportPage/store';
import {logHandledError} from './../../services/errors/errorReporting';
import {WBSlateElementType} from './WBSlate/types';
import {isTextWithComments} from './WBSlate/utils';
import {WBSlate} from './WBSlate/WBSlate';
import {
  WBSlateReduxBridgeContext,
  WBSlateReduxBridgeDebouncedProps,
} from './WBSlateReduxBridgeContext';

const WBSlateReduxBridgeDebounced: React.FC<WBSlateReduxBridgeDebouncedProps> =
  React.memo(({viewRef, view, projectName, entityName, viewId, readOnly}) => {
    const dispatch = useDispatch();
    const {hasInlineComments, setHasInlineComments, setIsSyncingToRedux} =
      useReportState(state => ({
        hasInlineComments: state.hasInlineComments,
        setHasInlineComments: state.setHasInlineComments,
        setIsSyncingToRedux: state.setIsSyncingToRedux,
      }));
    const viewPartRef = useSelector(state => getReportRef(viewRef)(state));
    const viewPart = useSelector(state => getReportPart(viewRef)(state));
    const {blocks, panelSettingsRef, width} = viewPart;

    const reduxValue = React.useMemo(
      () => ({
        entityName,
        projectName,
        viewId,
        panelSettingsRef,
        viewRef,
        view,
      }),
      [entityName, projectName, viewId, panelSettingsRef, viewRef, view]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updateRedux = React.useCallback(
      _.debounce((newValue: WBSlateElementType[]) => {
        dispatch(setBlocks(viewPartRef, newValue));
        setIsSyncingToRedux(false);
      }, 1000),
      [dispatch, viewPartRef]
    );

    const [value, setValue] = React.useState<WBSlateElementType[]>(blocks);
    const onChange = React.useCallback(
      (newValue: WBSlateElementType[]) => {
        // For some reason, this is fired whenever the selection changes.
        // Only update when the actual content changes.
        if (newValue !== value) {
          setValue(newValue);
          setIsSyncingToRedux(true);
          updateRedux(newValue);
        }
      },
      [value, updateRedux, setIsSyncingToRedux]
    );

    React.useEffect(() => {
      try {
        /**
         * a report is built of blocks, a block is made of children, some children have a property that's `inlineComment_[hash]`. If we can find a single one in the report then we need to fetch the discussions
         */
        const hasComments = value
          .flatMap(block => block.children)
          .some(isTextWithComments);

        if (hasInlineComments !== !!hasComments) {
          setHasInlineComments(!!hasComments);
        }
      } catch (e) {
        // if anything fails
        logHandledError(
          'Unable to determine if report blocks include inline comments'
        );
        if (!hasInlineComments) {
          // defensively set this to true so that we fetch the comments if they exist
          setHasInlineComments(true);
        }
      }
    }, [value, hasInlineComments, setHasInlineComments]);

    return (
      <WBSlateReduxBridgeContext.Provider value={reduxValue}>
        <WBSlate
          onChange={onChange}
          readOnly={readOnly}
          value={value}
          view={view}
          viewID={viewId}
          viewRef={viewRef}
          widthMode={width}
        />
      </WBSlateReduxBridgeContext.Provider>
    );
  });

export default WBSlateReduxBridgeDebounced;

export type WBSlateReduxBridgeDisabledProps = Pick<
  WBSlateReduxBridgeDebouncedProps,
  `entityName` | `projectName` | `viewId` | `readOnly`
> & {
  value: WBSlateElementType[];
  onChange: (newValue: WBSlateElementType[]) => void;
};

export const WBSlateReduxBridgeDisabled: React.FC<WBSlateReduxBridgeDisabledProps> =
  React.memo(({projectName, entityName, viewId, readOnly, value, onChange}) => {
    const reduxValue = React.useMemo(
      () => ({
        entityName,
        projectName,
        viewId,
        panelSettingsRef: null,
        viewRef: null,
        view: null,
      }),
      [entityName, projectName, viewId]
    );

    return (
      <WBSlateReduxBridgeContext.Provider value={reduxValue}>
        <WBSlate
          readOnly={readOnly}
          value={value}
          widthMode="profile"
          viewID={viewId}
          viewRef={null}
          view={null}
          onChange={onChange}
        />
      </WBSlateReduxBridgeContext.Provider>
    );
  });
